import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  OnInit,
  ViewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormGroup, ReactiveFormsModule } from '@angular/forms';
import { ScrollElements } from '@app/core/forms/directives/scroll-ements';
import { ScrollToInvalidFieldOnFormSubmitDirective } from '@app/core/forms/directives/scroll-to-invalid-field-on-form-submit.directive';
import { applyErrorsToForm } from '@app/core/helpers/apply-errors-to-form';
import { extractHttpErrorResponse } from '@app/core/helpers/extract-http-error-response';
import { NotificationsService } from '@app/core/notifications/services/notifications.service';
import { BillingInformationComponent } from '@app/layout/forms/billing-information/billing-information.component';
import { PaymentInformationComponent } from '@app/layout/forms/payment-information/payment-information.component';
import { NavService } from '@app/services/nav.service';
import { LoadProfile, UpdateProfile } from '@app/store';
import { deepCopy } from '@app/utils/deep-copy';
import {
  ActionCompletion,
  Actions,
  ofActionCompleted,
  Store,
} from '@ngxs/store';
import { tap } from 'rxjs';
import { AccountAvatarComponent } from './account-avatar/account-avatar.component';
import { AccountInformationComponent } from './account-information/account-information.component';
import { AccountSaveInformationComponent } from './account-save-information/account-save-information.component';

@Component({
  selector: 'app-account-profile-page',
  templateUrl: './account-profile.page.html',
  styleUrls: ['./account-profile.page.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    AccountInformationComponent,
    AccountAvatarComponent,
    BillingInformationComponent,
    PaymentInformationComponent,
    AccountSaveInformationComponent,
    ScrollToInvalidFieldOnFormSubmitDirective,
    ReactiveFormsModule,
  ],
})
export class AccountProfilePage implements OnInit, AfterViewInit {
  constructor(
    private navService: NavService,
    private store: Store,
    private actions$: Actions,
    private destroyRef: DestroyRef,
    private notificationsService: NotificationsService,
  ) {}

  protected form: FormGroup = new FormGroup({});

  @ViewChild(ScrollToInvalidFieldOnFormSubmitDirective)
  private scrollDirective!: ScrollToInvalidFieldOnFormSubmitDirective;

  @ViewChild(AccountInformationComponent)
  private accountInformationComponent!: AccountInformationComponent;

  @ViewChild(AccountAvatarComponent)
  private accountAvatarComponent!: AccountAvatarComponent;

  @ViewChild(BillingInformationComponent)
  private billingInformationComponent!: BillingInformationComponent;

  @ViewChild(PaymentInformationComponent)
  private paymentInformationComponent!: PaymentInformationComponent;

  protected ScrollElements = ScrollElements;

  ngOnInit(): void {
    this.setNavigation();
    this.store.dispatch(new LoadProfile());
    this.handleBackendResponse();
  }

  ngAfterViewInit(): void {
    this.extendForm();
  }

  private setNavigation(): void {
    this.navService.setNavItems([
      { label: 'Account', route: '/account' },
      { label: 'Profile' },
    ]);
  }

  private handleBackendResponse(): void {
    this.actions$
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        ofActionCompleted(UpdateProfile),
        tap((actionCompletion: ActionCompletion) => {
          if (actionCompletion.result.successful) {
            this.notificationsService.displaySuccessNotification(
              'Successfully saved!',
              'The information you entered has been successfully saved!',
            );
          } else {
            const errors = extractHttpErrorResponse(actionCompletion);
            applyErrorsToForm(errors, this.form);

            this.scrollDirective.scrollToFirstInvalidControl();
          }
        }),
      )
      .subscribe();
  }

  protected extendForm(): void {
    this.form.addControl('profile', this.accountInformationComponent.form);
    (this.form.get('profile') as FormGroup).addControl(
      'file_id',
      this.accountAvatarComponent.fc,
    );
    this.form.addControl('billing_info', this.billingInformationComponent.form);
    this.form.addControl('payment_info', this.paymentInformationComponent.form);
  }

  protected onSubmit(): void {
    this.form.markAllAsTouched();
    if (this.form.invalid) {
      return;
    }

    this.store.dispatch(new UpdateProfile(deepCopy(this.form.value)));
  }
}
